import posthog from 'posthog-js';
import {
  isAnalyticsOptedOut,
  isInternalEmail,
  optInAnalyticsInBrowser,
  optOutAnalyticsInBrowser,
} from './opt-out';
import type {
  AnalyticsApp,
  AnalyticsEventName,
  AnalyticsEventPayloads,
  AnalyticsProperties,
} from './types';

interface InitializePostHogOptions {
  app: AnalyticsApp;
  key?: string;
  apiHost?: string;
}

interface IdentifyPostHogUserOptions {
  app: AnalyticsApp;
  id: number | string;
  email: string | null | undefined;
  firstName?: string | null;
  lastName?: string | null;
}

export function initializePrivacyFirstPostHog({
  app,
  key,
  apiHost = '/ingest',
}: InitializePostHogOptions): void {
  if (!key) return;
  if (typeof document !== 'undefined' && isAnalyticsOptedOut(document.cookie)) return;

  posthog.init(key, {
    api_host: apiHost,
    defaults: '2026-01-30',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    before_send: (event) => {
      if (!event) return null;

      const properties: AnalyticsProperties = {
        ...(event.properties as AnalyticsProperties | undefined),
        app,
      };

      return { ...event, properties };
    },
  });
}

export async function identifyPostHogUser({
  app,
  id,
  email,
  firstName,
  lastName,
}: IdentifyPostHogUserOptions): Promise<void> {
  const normalizedEmail = email?.trim().toLowerCase();

  if (!normalizedEmail) return;

  if (isInternalEmail(normalizedEmail)) {
    optOutAnalyticsInBrowser();
    return;
  }

  const normalizedFirstName = firstName?.trim() || undefined;
  const normalizedLastName = lastName?.trim() || undefined;
  const name = [normalizedFirstName, normalizedLastName].filter(Boolean).join(' ') || undefined;

  await optInAnalyticsInBrowser();

  posthog.identify(`user:${id}`, {
    app,
    email: normalizedEmail,
    first_name: normalizedFirstName,
    last_name: normalizedLastName,
    name,
  });
}

export function resetPostHogUser(): void {
  posthog.reset();
}

export function getPostHogDistinctId(): string | undefined {
  return posthog.get_distinct_id() || undefined;
}

export function captureSafePostHogEvent<EventName extends AnalyticsEventName>(
  event: EventName,
  properties: AnalyticsEventPayloads[EventName]
): void {
  posthog.capture(event, properties);
}
